import { CacheApi, CacheStrategy } from "./CacheApi";

export interface StorefrontApiParams {
  version: string;
  language: string;
  country: string;
  storeName: string;
  accessToken: string;
}

export class StorefrontApi {
  #host: string;
  #headers: HeadersInit & Record<string, string>;
  #language: string;
  #country: string;
  #cache: CacheApi;
  #productMetafields: { namespace: string; key: string }[] = [];
  #variantMetafields: { namespace: string; key: string }[] = [];

  constructor(params: StorefrontApiParams) {
    this.#country = params.country;
    this.#language = params.language;
    this.#host = `https://${params.storeName}.myshopify.com/api/${params.version}/graphql.json`;
    this.#headers = {
      accept: "application/json",
      "content-type": "application/json",
      "x-shopify-storefront-access-token": params.accessToken,
    };
    this.#cache = new CacheApi({ name: "storefront" });
  }

  setFields({
    productMetafields,
    variantMetafields,
  }: {
    productMetafields?: { namespace: string; key: string }[];
    variantMetafields?: { namespace: string; key: string }[];
  }) {
    if (productMetafields) {
      this.#productMetafields = productMetafields;
    }
    if (variantMetafields) {
      this.#variantMetafields = variantMetafields;
    }
  }

  loadProduct({
    productId,
    cacheStrategy = CacheStrategy.Short,
  }: {
    productId: number;
    cacheStrategy?: CacheStrategy;
  }) {
    const productMetafieldsFragment = `
      fragment productMetafields on Product {
        metafields(identifiers: [${this.#productMetafields
          .map(
            (metafield) =>
              `{namespace: "${metafield.namespace}", key: "${metafield.key}"}`,
          )
          .join(",")}]) {
          key
          value
          namespace
        }
      }
    `;

    const variantMetafieldsFragment = `
      fragment variantMetafields on ProductVariant {
        metafields(identifiers: [${this.#variantMetafields
          .map(
            (metafield) =>
              `{namespace: "${metafield.namespace}", key: "${metafield.key}"}`,
          )
          .join(",")}]) {
          key
          value
          namespace
        }
      }
    `;

    return this.fetch({
      cacheStrategy,
      body: JSON.stringify({
        query: `
          query @inContext(language: ${this.#language}, country: ${this.#country}) {
            product(id: "gid://shopify/Product/${productId}") {
              ...productFields
              ...productMetafields
            }
          }
          ${PRODUCT_FIELDS}
          ${productMetafieldsFragment}
          ${variantMetafieldsFragment}
        `,
      }),
    }).then(({ data, errors }) => {
      if (errors) {
        console.warn("Could not load product data", errors);
        return null;
      }
      return data.product as Product | null;
    });
  }

  loadProductIdByHandle({ productHandle }: { productHandle: string }) {
    return this.fetch({
      cacheStrategy: CacheStrategy.Long,
      body: JSON.stringify({
        query: `
          query @inContext(language: ${this.#language}, country: ${this.#country}) {
            productByHandle(handle: "${productHandle}") {
              id
            }
          }
        `,
      }),
    }).then(({ data, errors }) => {
      if (errors) {
        console.warn("Could not load product id", errors);
        return 0;
      }
      if (!data.productByHandle) {
        return 0;
      }
      return parseInt(data.productByHandle?.id.split("/").pop() as string);
    });
  }

  fetch<T = any>(params: FetchParams) {
    const headers = {
      ...this.#headers,
      ...params.headers,
    };
    const body = params.body;
    const cacheKey = [this.#host, body, headers];

    return this.#cache.withCache(
      cacheKey,
      params.cacheStrategy,
      async (signal) => {
        const response = await window.fetch(this.#host, {
          method: "POST",
          headers,
          body,
          signal,
        });
        if (response.ok) {
          return response.json() as T;
        } else {
          throw Error(`Error ${response.status}`);
        }
      },
    );
  }
}

export interface FetchParams {
  body?: string;
  headers?: Record<string, any>;
  cacheStrategy: CacheStrategy;
}

export interface Product {
  availableForSale: boolean;
  compareAtPriceRange: PriceRange;
  featuredImage: Image | null;
  handle: string;
  id: string;
  images: Connection<Image>;
  onlineStoreUrl: string | null;
  options: ProductOption[];
  priceRange: PriceRange;
  productType: string;
  sku: string;
  tags: string[];
  title: string;
  variants: Connection<ProductVariant>;
  vendor: string;
  metafields?: {
    namespace: string;
    key: string;
    value?: string;
  }[];
}

export interface PriceRange {
  maxVariantPrice: Money;
  minVariantPrice: Money;
}

export interface Money {
  amount: string;
}

export interface Image {
  altText: string | null;
  url: string;
}

export interface ProductOption {
  name: string;
  values: string[];
  optionValues: ProductOptionValue[];
}

export interface ProductOptionValue {
  id: string;
  name: string;
  swatch: ProductOptionValueSwatch | null;
}

export interface ProductOptionValueSwatch {
  color: string | null;
  image: Media | null;
}

export interface Media {
  id: string;
  alt: string | null;
  image: Image | null;
}

export interface ProductVariant {
  availableForSale: boolean;
  compareAtPrice: Money | null;
  currentlyNotInStock: boolean;
  id: string;
  image: Image | null;
  price: Money;
  selectedOptions: SelectedOption[];
  sku: string;
  title: string;
  unitPrice: Money | null;
  metafields?: {
    namespace: string;
    key: string;
    value?: string;
  }[];
}

export interface SelectedOption {
  name: string;
  value: string;
}

export interface Connection<T> {
  nodes: T[];
}

// quantityAvailable (requires: unauthenticated_read_product_inventory)
// When overselling: currentlyNotInStock=false, availableForSale=true
const VARIANT_FIELDS = `
  fragment variantFields on ProductVariant {
    availableForSale
    compareAtPrice {
      amount
    }
    currentlyNotInStock
    id
    image {
      altText
      url
    }
    price {
      amount
    }
    selectedOptions {
      name
      value
    }
    title
    unitPrice {
      amount
    }
    sku
  }
`;

const PRODUCT_FIELDS = `
  ${VARIANT_FIELDS}
  fragment productFields on Product {
    availableForSale
    compareAtPriceRange {
      maxVariantPrice {
        amount
      }
      minVariantPrice {
        amount
      }
    }
    featuredImage {
      altText
      url
    }
    handle
    id
    onlineStoreUrl
    options {
      name
      values
      optionValues {
        id
        name
        swatch {
          color
          image {
            id
            alt
            ...on MediaImage {
              image {
                url
              }
            }
          }
        }
      }
    }
    priceRange {
      maxVariantPrice {
        amount
      }
      minVariantPrice {
        amount
      }
    }
    productType
    tags
    title
    vendor
    images(first: 100) {
      nodes {
        altText
        url
      }
    }
    variants(first: 250) {
      nodes {
        ...variantFields
        ...variantMetafields
      }
    }
  }
`;
